import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, setDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import useSponsors from '../hooks/useSponsors';
import {
  normalizeSponsorWebsite,
  sponsorSlug,
  sponsorWritePayload,
  staticSponsorsAsDocs,
} from '../utils/sponsorsCatalog';
import './SponsorsAdmin.css';

const LOGO_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'svg'];

const emptyForm = () => ({
  name: '',
  website: '',
  logoUrl: '',
  sortOrder: '',
  active: true,
});

function logoExtension(file) {
  return (file.name.split('.').pop() || '').toLowerCase();
}

function isLogoFile(file) {
  const ext = logoExtension(file);
  const typeOk = !file.type || file.type.startsWith('image/');
  return LOGO_EXTS.includes(ext) && typeOk;
}

function logoContentType(file, ext) {
  if (ext === 'svg') return 'image/svg+xml';
  if (file.type && file.type.startsWith('image/')) return file.type;
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  return 'image/jpeg';
}

export default function SponsorsAdmin() {
  const { sponsors, source, loading, error } = useSponsors({ includeInactive: true });
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filter, setFilter] = useState('');

  const visibleCount = sponsors.filter((s) => s.active).length;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sponsors;
    return sponsors.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        String(s.website || '')
          .toLowerCase()
          .includes(q)
    );
  }, [sponsors, filter]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setLogoFile(null);
    setLogoPreview('');
  };

  const startEdit = (sponsor) => {
    setEditingId(sponsor.id);
    setForm({
      name: sponsor.name,
      website: sponsor.website || '',
      logoUrl: sponsor.logoUrl || '',
      sortOrder: sponsor.sortOrder === 0 || sponsor.sortOrder ? String(sponsor.sortOrder) : '',
      active: sponsor.active !== false,
    });
    setLogoFile(null);
    setLogoPreview('');
    setMessage({ type: '', text: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveSponsor = async (fields, { existing, docId, slug }) => {
    const payload = sponsorWritePayload(fields, { existingSlug: slug });
    payload.updatedAt = Timestamp.now();
    payload.updatedBy = auth.currentUser?.uid || '';
    await setDoc(doc(db, 'sponsors', docId), payload, { merge: true });
    return payload;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    const name = String(form.name || '').trim();
    if (!name) {
      setMessage({ type: 'error', text: 'Sponsor name is required.' });
      return;
    }

    const websiteRaw = String(form.website || '').trim();
    if (websiteRaw && !normalizeSponsorWebsite(websiteRaw)) {
      setMessage({ type: 'error', text: 'Website must be a valid http or https link.' });
      return;
    }

    if (logoFile && !isLogoFile(logoFile)) {
      setMessage({ type: 'error', text: 'Logo must be a PNG, JPG, WEBP, or SVG image.' });
      return;
    }
    if (logoFile && logoFile.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Logo must be 10 MB or smaller.' });
      return;
    }
    if (!logoFile && !form.logoUrl) {
      setMessage({ type: 'error', text: 'A logo image is required.' });
      return;
    }

    const existing = editingId ? sponsors.find((s) => s.id === editingId) : null;
    const slug = existing?.sponsorSlug || sponsorSlug(name);
    const docId = existing?.id || slug;
    if (!slug) {
      setMessage({ type: 'error', text: 'Name needs at least one letter or number.' });
      return;
    }
    if (!existing && sponsors.some((s) => s.id === docId)) {
      setMessage({
        type: 'error',
        text: 'A sponsor with that name already exists. Edit the existing sponsor instead.',
      });
      return;
    }

    setSaving(true);
    try {
      let logoUrl = form.logoUrl || '';
      if (logoFile) {
        const user = auth.currentUser;
        if (!user) throw new Error('Not signed in');
        const ext = logoExtension(logoFile);
        const path = `sponsorLogos/${user.uid}/${slug}/${Date.now()}.${ext}`;
        const sref = ref(storage, path);
        await uploadBytes(sref, logoFile, { contentType: logoContentType(logoFile, ext) });
        logoUrl = await getDownloadURL(sref);
      }

      await saveSponsor(
        {
          ...form,
          name,
          logoUrl,
          sortOrder: form.sortOrder === '' ? sponsors.length + 1 : Number(form.sortOrder),
        },
        { existing, docId, slug }
      );
      setMessage({
        type: 'success',
        text: existing ? `Updated ${name}.` : `Added ${name} to the sponsor wall.`,
      });
      resetForm();
    } catch (err) {
      console.error(err);
      setMessage({
        type: 'error',
        text: 'Could not save sponsor. Deploy the latest Firestore and Storage rules if this is the first time using Sponsors.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (sponsor) => {
    const nextActive = !sponsor.active;
    const ok = window.confirm(
      nextActive
        ? `Show ${sponsor.name} on the home page?`
        : `Hide ${sponsor.name} on the home page? The logo stays saved here.`
    );
    if (!ok) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const slug = sponsor.sponsorSlug || sponsorSlug(sponsor.name);
      await saveSponsor(
        {
          name: sponsor.name,
          website: sponsor.website || '',
          logoUrl: sponsor.logoUrl || '',
          sortOrder: sponsor.sortOrder,
          active: nextActive,
        },
        { existing: sponsor, docId: sponsor.id, slug }
      );
      if (editingId === sponsor.id) {
        setForm((prev) => ({ ...prev, active: nextActive }));
      }
      setMessage({
        type: 'success',
        text: nextActive ? `${sponsor.name} is on the home page.` : `${sponsor.name} is hidden.`,
      });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Could not update that sponsor.' });
    } finally {
      setSaving(false);
    }
  };

  const handleImportBuiltIn = async () => {
    const ok = window.confirm(
      'Import the 2026–2027 sponsors into Firestore so edits persist? Existing sponsors are left as they are.'
    );
    if (!ok) return;
    setSeeding(true);
    setMessage({ type: '', text: '' });
    try {
      const seed = staticSponsorsAsDocs();
      const existingSnap = await getDocs(collection(db, 'sponsors'));
      const existingIds = new Set(existingSnap.docs.map((d) => d.id));
      let written = 0;
      let batch = writeBatch(db);
      let ops = 0;
      for (const s of seed) {
        if (existingIds.has(s.id)) continue;
        const payload = sponsorWritePayload(s, { existingSlug: s.sponsorSlug });
        payload.updatedAt = Timestamp.now();
        payload.updatedBy = auth.currentUser?.uid || 'seed';
        batch.set(doc(db, 'sponsors', s.id), payload, { merge: false });
        written += 1;
        ops += 1;
        if (ops >= 400) {
          await batch.commit();
          batch = writeBatch(db);
          ops = 0;
        }
      }
      if (ops > 0) await batch.commit();
      setMessage({
        type: 'success',
        text:
          written === 0
            ? 'All built-in sponsors are already in Firestore.'
            : `Imported ${written} sponsor${written === 1 ? '' : 's'} into Firestore.`,
      });
    } catch (err) {
      console.error(err);
      setMessage({
        type: 'error',
        text: 'Import failed. Deploy the latest Firestore rules for sponsors, then try again.',
      });
    } finally {
      setSeeding(false);
    }
  };

  const previewSrc = logoPreview || form.logoUrl;

  return (
    <div className="sponsors-admin">
      <h2 className="admin-main-section-title">Sponsors</h2>
      <p className="sponsors-admin-lead">
        Manage the thank-you logo wall on the home page. Add a name, logo, and optional website.
        Hide a sponsor to take the logo off the site without deleting it.
      </p>

      <div className="sponsors-admin-toolbar">
        <div className="sponsors-admin-source">
          {loading ? (
            <span>Loading sponsors…</span>
          ) : source === 'firestore' ? (
            <span>
              Live source: <strong>Firestore</strong> ({sponsors.length} saved, {visibleCount} on the home page)
            </span>
          ) : source === 'hybrid' ? (
            <span>
              Live source: <strong>Firestore + built-in</strong> ({sponsors.length} saved, {visibleCount} on the home page)
            </span>
          ) : (
            <span>
              Live source: <strong>built-in list</strong> ({sponsors.length} saved, {visibleCount} on the home page)
            </span>
          )}
        </div>
        <button type="button" className="admin-button" disabled={seeding || saving} onClick={handleImportBuiltIn}>
          {seeding ? 'Importing…' : 'Import built-in sponsors'}
        </button>
      </div>

      {error ? (
        <p className="sponsors-admin-message is-error">
          Could not reach Firestore, so the built-in 2026–2027 list is showing. Deploy the latest rules before saving changes.
        </p>
      ) : null}

      {message.text ? (
        <p className={`sponsors-admin-message ${message.type === 'success' ? 'is-success' : 'is-error'}`}>
          {message.text}
        </p>
      ) : null}

      <form className="sponsors-admin-form" onSubmit={handleSave}>
        <h3>{editingId ? 'Edit sponsor' : 'Add sponsor'}</h3>
        <div className="sponsors-admin-form-grid">
          <label>
            Name
            <input
              className="admin-input"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
              maxLength={120}
              placeholder="Business name"
            />
          </label>
          <label>
            Website
            <input
              className="admin-input"
              value={form.website}
              onChange={(e) => updateField('website', e.target.value)}
              placeholder="https://example.com"
              inputMode="url"
            />
          </label>
          <label>
            Sort order
            <input
              className="admin-input"
              type="number"
              min="0"
              max="10000"
              value={form.sortOrder}
              onChange={(e) => updateField('sortOrder', e.target.value)}
              placeholder="1"
            />
          </label>
        </div>

        <div className="sponsors-admin-logo-row">
          {previewSrc ? (
            <img src={previewSrc} alt="" className="sponsors-admin-logo-preview" />
          ) : (
            <div className="sponsors-admin-logo-preview is-empty">No logo yet</div>
          )}
          <div>
            <p className="sponsors-admin-hint">
              Wide logos work best. PNG, JPG, WEBP, or SVG, max 10 MB. The home page shows them on a light background.
            </p>
            <label className="sponsors-admin-file">
              <span className="admin-button">Choose logo</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
                hidden
                onChange={(e) => {
                  setLogoFile(e.target.files?.[0] || null);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        </div>

        <label className="sponsors-admin-check">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => updateField('active', e.target.checked)}
          />
          Show on the home page
        </label>

        <div className="sponsors-admin-form-actions">
          <button type="submit" className="admin-button" disabled={saving || seeding}>
            {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add sponsor'}
          </button>
          {editingId ? (
            <button type="button" className="admin-button" disabled={saving} onClick={resetForm}>
              Cancel edit
            </button>
          ) : null}
        </div>
      </form>

      <div className="sponsors-admin-list-header">
        <h3 className="sponsors-admin-list-title">Sponsors ({sponsors.length})</h3>
        <input
          className="admin-input sponsors-admin-filter"
          placeholder="Filter by name or website…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="sponsors-admin-table-wrap">
        <table className="admin-table sponsors-admin-table">
          <thead>
            <tr>
              <th className="sponsors-admin-th-logo">Logo</th>
              <th>Name</th>
              <th className="sponsors-admin-th-order">Order</th>
              <th className="sponsors-admin-th-status">Status</th>
              <th className="sponsors-admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ color: '#888' }}>
                  No sponsors yet.
                </td>
              </tr>
            ) : (
              filtered.map((s) => (
                <tr key={s.id} className={s.active ? '' : 'sponsors-admin-row is-hidden'}>
                  <td className="sponsors-admin-td-logo">
                    {s.logoUrl ? (
                      <img src={s.logoUrl} alt="" className="sponsors-admin-thumb" />
                    ) : (
                      <span style={{ color: '#888' }}>—</span>
                    )}
                  </td>
                  <td>
                    <span className="sponsors-admin-name">{s.name}</span>
                    {s.website ? (
                      <span className="sponsors-admin-website" title={s.website}>
                        {s.website}
                      </span>
                    ) : null}
                  </td>
                  <td className="sponsors-admin-td-order">{s.sortOrder}</td>
                  <td className="sponsors-admin-td-status">
                    <span className={`sponsors-admin-status${s.active ? '' : ' is-hidden'}`}>
                      {s.active ? 'Visible' : 'Hidden'}
                    </span>
                  </td>
                  <td className="sponsors-admin-td-actions">
                    <div className="sponsors-admin-row-actions">
                      <button
                        type="button"
                        className="admin-button sponsors-admin-action-btn"
                        onClick={() => startEdit(s)}
                        disabled={saving}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="admin-button-archive sponsors-admin-action-btn"
                        onClick={() => handleToggleActive(s)}
                        disabled={saving}
                      >
                        {s.active ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
